/**
 * EditorPage - Workspace wrapper with Supabase integration
 *
 * Loads a project from Supabase (or from navigation state for new projects)
 * and renders the Workspace editor with auto-save capability.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { loadProject, saveProject } from '@/services/projectService';
import { getProfile } from '@/services/profileService';
import { Slide, Profile, CarouselStyle, AspectRatio, CarouselProject, Language, AnalisePreCriacao, ChecklistValidacao } from '@/types';
import { MOCK_SLIDES } from '@/constants';
import { extractProjectNameFromCover, inferLegacyNameSource, ProjectNameSource } from '@/lib/projectName';
import { isOversizedDataUri } from '@/lib/imageCompression';
import { logger } from '@/lib/logger';
import { generateId } from '@/lib/utils';
import Workspace from '@/components/Workspace';
import HelpChat from '@/components/help/HelpChat';
import LoadingScreen from '@/components/ui/loading-screen';

type EditorTheme = 'light' | 'dark';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const EditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [slides, setSlides] = useState<Slide[]>(MOCK_SLIDES);
  const [profile, setProfile] = useState<Profile>({
    name: '', handle: '', avatarUrl: ''
  });
  const [style, setStyle] = useState<CarouselStyle>(CarouselStyle.TWITTER);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('4/5');
  const [projectId, setProjectId] = useState<string>(id || generateId());
  const [projectName, setProjectName] = useState('');
  const [projectNameSource, setProjectNameSource] = useState<ProjectNameSource>('cover_auto');
  const [editorTheme, setEditorTheme] = useState<EditorTheme>('dark');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [analisePreCriacao, setAnalisePreCriacao] = useState<AnalisePreCriacao | undefined>();
  const [checklistValidacao, setChecklistValidacao] = useState<ChecklistValidacao | undefined>();

  // Auto-save refs
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Thumbnail for dashboard preview
  const thumbnailRef = useRef<string | null>(null);

  // Apply editor theme
  useEffect(() => {
    if (editorTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [editorTheme]);

  // Auto-generate project name from first slide's first sentence
  useEffect(() => {
    if (slides.length === 0 || projectNameSource === 'manual') return;

    const generatedName = extractProjectNameFromCover(slides);
    if (!generatedName) return;

    setProjectName(prev => prev !== generatedName ? generatedName : prev);
    setProjectNameSource(prev => prev !== 'cover_auto' ? 'cover_auto' : prev);
  }, [slides]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load project data and profile from cai_profiles
  useEffect(() => {
    const loadData = async () => {
      // Always load the latest profile from cai_profiles
      let userProfile: Profile = { name: '', handle: '', avatarUrl: '' };
      try {
        if (!user) throw new Error('No user');
        const savedProfile = await getProfile(user.id);
        if (savedProfile && savedProfile.handle) {
          userProfile = {
            name: savedProfile.name,
            handle: savedProfile.handle,
            avatarUrl: savedProfile.avatarUrl,
          };
        }
      } catch (err) {
        logger.warn('Failed to load user profile');
      }

      // Check if we have state from CreatePage (new project)
      const state = location.state as {
        slides?: Slide[];
        profile?: Profile;
        style?: CarouselStyle;
        aspectRatio?: AspectRatio;
        isNew?: boolean;
        language?: Language;
        analisePreCriacao?: AnalisePreCriacao;
        checklistValidacao?: ChecklistValidacao;
      } | null;

      if (state?.isNew && state.slides) {
        setSlides(state.slides);
        // Use profile from cai_profiles (source of truth), fallback to state
        setProfile(userProfile.handle ? userProfile : (state.profile || userProfile));
        setStyle(state.style || CarouselStyle.TWITTER);
        setAspectRatio(state.aspectRatio || '4/5');
        setAnalisePreCriacao(state.analisePreCriacao);
        setChecklistValidacao(state.checklistValidacao);
        setProjectId(id || generateId());
        setProjectNameSource('cover_auto');
        setLoading(false);
        return;
      }

      // Load from Supabase
      if (id) {
        try {
          const project = await loadProject(id);

          setSlides(project.slides);
          // Use profile from cai_profiles (always up-to-date), fallback to project data
          setProfile(userProfile.handle ? userProfile : project.profile);
          setStyle(project.style);
          setAspectRatio(project.aspectRatio);
          setProjectId(project.id);
          setProjectName(project.name);
          setProjectNameSource(project.nameSource || inferLegacyNameSource(project.name));
          setAnalisePreCriacao(project.analisePreCriacao);
          setChecklistValidacao(project.checklistValidacao);

          if (project.thumbnailUrl) {
            thumbnailRef.current = project.thumbnailUrl;
          }

          setLoading(false);
        } catch (error) {
          logger.error('Failed to load project');
          alert(t('editor.loadFailed'));
          navigate('/');
        }
      } else {
        setProfile(userProfile);
        setLoading(false);
      }
    };

    loadData();
  }, [id, location.state, navigate, user]);

  // Auto-save debounced (2 seconds after last change)
  const handleAutoSave = useCallback((updatedSlides: Slide[]) => {
    if (!user || !projectId) return;

    // Cancel previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save by 2 seconds
    saveTimeoutRef.current = setTimeout(async () => {
      // Removed broken hash check - allow all saves to proceed
      // (saves are already debounced by 2 seconds, so not a performance issue)

      try {
        setSaveStatus('saving');
        let thumbnailToSave: string | undefined = thumbnailRef.current || undefined;

        if (isOversizedDataUri(thumbnailToSave)) {
          logger.warn('Oversized thumbnail data URI discarded to protect save');
          thumbnailToSave = undefined;
        }

        // Strip oversized data URIs from slide images to prevent 413
        const slidesToSave = updatedSlides.map(slide => {
          const cleaned = { ...slide };
          if (isOversizedDataUri(cleaned.imageUrl, 500 * 1024)) {
            logger.warn('Oversized slide imageUrl stripped from save payload');
            cleaned.imageUrl = undefined;
          }
          if (isOversizedDataUri(cleaned.backgroundImageUrl, 500 * 1024)) {
            logger.warn('Oversized slide backgroundImageUrl stripped from save payload');
            cleaned.backgroundImageUrl = undefined;
          }
          return cleaned;
        });

        const generatedName = extractProjectNameFromCover(slidesToSave);
        const locale = language === 'pt-BR' ? 'pt-BR' : language === 'es' ? 'es' : 'en';
        const formattedDate = new Date().toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
        const resolvedProjectName =
          projectName ||
          generatedName ||
          t('editor.defaultProjectName', { date: formattedDate });

        const payloadEstimate = JSON.stringify({
          slides: slidesToSave,
          thumbnailUrl: thumbnailToSave
        });
        const payloadSizeKB = (payloadEstimate.length / 1024).toFixed(2);

        if (parseFloat(payloadSizeKB) > 15000) {
          alert(t('editor.projectTooLarge', { size: payloadSizeKB }));
          logger.error('Payload exceeds safe size limit');
        }

        await saveProject({
          id: projectId,
          name: resolvedProjectName,
          style,
          aspectRatio,
          profile,
          slides: slidesToSave,
          thumbnailUrl: thumbnailToSave,
          theme: style === CarouselStyle.STORYTELLER ? 'DARK' : 'LIGHT',
          accentColor: '#4763CA',
          showAccent: true,
          showSlideNumbers: true,
          showVerifiedBadge: true,
          headerScale: 1.0,
          fontStyle: 'MODERN',
          fontScale: 1.0,
          globalImageStyle: '',
          nameSource: projectNameSource,
          language,
          analisePreCriacao,
          checklistValidacao,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }, user.id);

        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2500);
      } catch (err) {
        logger.error('Save failed');
        setSaveStatus('error');
        alert(t('editor.saveFailed'));
      }
    }, 2000);
  }, [user, projectId, projectName, projectNameSource, style, aspectRatio, profile, language, analisePreCriacao, checklistValidacao, t]);

  // Wrap onUpdateSlides to include auto-save
  const handleUpdateSlides = useCallback((newSlides: Slide[]) => {
    setSlides(newSlides);
    handleAutoSave(newSlides);
  }, [handleAutoSave]);

  // Thumbnail callback from Workspace - store and trigger a save
  const handleThumbnailGenerated = useCallback((base64: string) => {
    thumbnailRef.current = base64;
    // Trigger a save to persist the new thumbnail immediately.
    // Thumbnail generation is async and completes AFTER the debounced auto-save,
    // so without this explicit save the thumbnail would only be persisted on the
    // next content change.
    handleAutoSave(slides);
  }, [handleAutoSave, slides]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return <LoadingScreen message={t('common.loading')} />;
  }

  const autoExportOnMount = new URLSearchParams(location.search).get('autoExport') === '1';

  return (
    <>
      <Workspace
        slides={slides}
        profile={profile}
        style={style}
        aspectRatio={aspectRatio}
        onUpdateSlides={handleUpdateSlides}
        onStyleChange={setStyle}
        onBack={() => navigate('/')}
        projectName={projectName}
        saveStatus={saveStatus}
        editorTheme={editorTheme}
        onEditorThemeToggle={() => setEditorTheme(prev => prev === 'light' ? 'dark' : 'light')}
        language={language}
        projectId={projectId}
        analisePreCriacao={analisePreCriacao}
        checklistValidacao={checklistValidacao}
        onThumbnailGenerated={handleThumbnailGenerated}
        autoExportOnMount={autoExportOnMount}
      />
      <HelpChat />
    </>
  );
};

export default EditorPage;
